import { db } from './db.js'
import {
  getObraConfig,
  getLancamentos,
  getDocumentos,
  addAlerta,
  getAlertaByChave,
  resolverAlertasByChave,
} from './crud.js'
import {
  decisoesQueTravemAgora,
  getCaixaDoMes,
  getCronograma,
  getDecisoes,
  getParcelas,
  getRitmoObra,
  resumirContrato,
  resumirCronograma,
} from './gestao.js'
import {
  FASES_CONSTRUTIVAS,
  MODULOS,
  RESERVA_CONTINGENCIA_PADRAO_PCT,
  ROUTES,
  SINAL_MAXIMO_PCT,
  contratoDetailPath,
} from '@utils/constants.js'

// ── Chave constants ───────────────────────────────────────────

const CHAVE_FIN_TOTAL = 'fin:total-excedido'
const CHAVE_FIN_RESERVA = 'fin:reserva-consumida'
const chaveFinCat = (cat: string) => `fin:cat:${cat}`
const chaveNfFaltando = (id: number) => `nf:faltando:${id}`
const chaveModuloSeq = (id: string) => `modulo:seq:${id}`
const chaveParcelaVencida = (contratoId: number, parcelaId: number) =>
  `contrato:parcela-vencida:${contratoId}:${parcelaId}`
const chaveParcelaVencendo = (contratoId: number, parcelaId: number) =>
  `contrato:parcela-vencendo:${contratoId}:${parcelaId}`
const chaveContratoAdiantado = (id: number) => `contrato:adiantado:${id}`
const chaveFaseAtrasada = (faseId: string) => `cronograma:fase-atrasada:${faseId}`
const CHAVE_OBRA_ATRASADA = 'cronograma:obra-atrasada'

/** Destino do alerta a partir da chave (legado sem contratoId cai na lista). */
export function rotaDoAlerta(chave?: string): string | null {
  if (!chave) return null
  if (chave.startsWith('fin:') || chave.startsWith('nf:')) return ROUTES.FINANCEIRO
  if (chave.startsWith('cronograma:')) return ROUTES.CRONOGRAMA
  if (chave.startsWith('decisao:')) return ROUTES.DECISOES
  if (chave.startsWith('diario:')) return ROUTES.DIARIO
  if (chave.startsWith('modulo:')) return ROUTES.MODULOS
  const adiantado = /^contrato:adiantado:(\d+)$/.exec(chave)
  if (adiantado) return contratoDetailPath(Number(adiantado[1]))
  const parcela = /^contrato:parcela-(?:vencida|vencendo):(\d+)(?::(\d+))?$/.exec(chave)
  if (parcela?.[2]) return contratoDetailPath(Number(parcela[1]))
  if (chave.startsWith('contrato:')) return ROUTES.CONTRATOS
  return null
}

const PARCELA_AVISO_DIAS = 3

// ── Core helper ───────────────────────────────────────────────

async function ensureAlerta(
  chave: string,
  data: Omit<Parameters<typeof addAlerta>[0], 'chave' | 'status' | 'lido'>,
): Promise<void> {
  const existing = await getAlertaByChave(chave)
  if (existing && (existing.status === 'ativo' || existing.status === 'ignorado')) return
  await addAlerta({ ...data, chave, status: 'ativo', lido: false })
}

// ── Alert generators ──────────────────────────────────────────

async function checkFinanceiro(): Promise<string[]> {
  const [config, lancamentos] = await Promise.all([getObraConfig(), getLancamentos()])
  if (!config) return []

  const orcamentoTotal = config.orcamentoTotal ?? 0
  const orcPorCat = config.orcamentoPorCategoria ?? {}
  const gastos = lancamentos.filter((l) => l.tipo !== 'receita')

  const gastoPorCat: Record<string, number> = {}
  for (const l of gastos) {
    gastoPorCat[l.categoria] = (gastoPorCat[l.categoria] ?? 0) + l.valor
  }

  const totalGasto = gastos.filter((l) => l.status === 'pago').reduce((s, l) => s + l.valor, 0)
  const alertasAtivos: string[] = []

  // Total budget exceeded
  if (orcamentoTotal > 0 && totalGasto > orcamentoTotal) {
    alertasAtivos.push(CHAVE_FIN_TOTAL)
    await ensureAlerta(CHAVE_FIN_TOTAL, {
      tipo: 'danger',
      titulo: 'Orçamento total excedido',
      mensagem: `O total gasto (${fmtCur(totalGasto)}) ultrapassou o orçamento total de ${fmtCur(orcamentoTotal)}.`,
    })
  }

  // Reserva de contingência consumida (gasto entrou na faixa reservada para imprevistos)
  const reservaPct = config.reservaContingenciaPct ?? RESERVA_CONTINGENCIA_PADRAO_PCT
  if (orcamentoTotal > 0 && reservaPct > 0 && totalGasto <= orcamentoTotal) {
    const limite = orcamentoTotal * (1 - reservaPct / 100)
    if (totalGasto > limite) {
      alertasAtivos.push(CHAVE_FIN_RESERVA)
      await ensureAlerta(CHAVE_FIN_RESERVA, {
        tipo: 'warning',
        titulo: 'Reserva para imprevistos em uso',
        mensagem: `Você já gastou ${fmtCur(totalGasto)}: entrou nos ${reservaPct}% reservados para imprevistos. Reveja o que falta contratar antes de novos pedidos.`,
      })
    }
  }

  // Per-category budget alerts
  for (const [cat, orcCat] of Object.entries(orcPorCat)) {
    if (orcCat <= 0) continue
    const gasto = gastoPorCat[cat] ?? 0
    const pct = gasto / orcCat
    const chave = chaveFinCat(cat)

    if (pct >= 1.0) {
      alertasAtivos.push(chave)
      await ensureAlerta(chave, {
        tipo: 'danger',
        titulo: `Orçamento excedido · ${cat}`,
        mensagem: `${fmtCur(gasto)} gasto de ${fmtCur(orcCat)} orçados (${Math.round(pct * 100)}%).`,
      })
    } else if (pct >= 0.8) {
      alertasAtivos.push(chave)
      await ensureAlerta(chave, {
        tipo: 'warning',
        titulo: `80% do orçamento · ${cat}`,
        mensagem: `${fmtCur(gasto)} de ${fmtCur(orcCat)} utilizados (${Math.round(pct * 100)}%).`,
      })
    }
  }

  return alertasAtivos
}

const NF_VALOR_MINIMO_PADRAO = 500

async function checkNfFaltando(): Promise<string[]> {
  const [config, lancamentos, documentos] = await Promise.all([
    getObraConfig(),
    getLancamentos(),
    getDocumentos(),
  ])
  const docLancIds = new Set(documentos.map((d) => d.lancamentoId).filter(Boolean))
  const minimo =
    config?.valorMinimoNF && config.valorMinimoNF > 0
      ? config.valorMinimoNF
      : NF_VALOR_MINIMO_PADRAO

  const semNF = lancamentos.filter(
    (l) => l.tipo !== 'receita' && l.valor > minimo && l.id != null && !docLancIds.has(l.id),
  )

  const alertasAtivos: string[] = []
  for (const l of semNF) {
    const chave = chaveNfFaltando(l.id!)
    alertasAtivos.push(chave)
    await ensureAlerta(chave, {
      tipo: 'warning',
      titulo: 'Nota fiscal ausente',
      mensagem: `Lançamento "${l.titulo}" (${fmtCur(l.valor)}) não tem documento vinculado.`,
    })
  }

  return alertasAtivos
}

async function checkModuloSequencia(): Promise<string[]> {
  const topicosAll = await db.topicos_progresso.toArray()

  const concluidosPorModulo: Record<string, number> = {}
  for (const t of topicosAll) {
    if (t.concluido) concluidosPorModulo[t.moduloId] = (concluidosPorModulo[t.moduloId] ?? 0) + 1
  }

  const alertasAtivos: string[] = []
  const ordenados = [...MODULOS].sort((a, b) => a.ordem - b.ordem)

  for (let i = 1; i < ordenados.length; i++) {
    const atual = ordenados[i]!
    const anterior = ordenados[i - 1]!
    const concluidosAtual = concluidosPorModulo[atual.id] ?? 0
    const concluidosAnterior = concluidosPorModulo[anterior.id] ?? 0

    if (concluidosAtual > 0 && concluidosAnterior === 0) {
      const chave = chaveModuloSeq(atual.id)
      alertasAtivos.push(chave)
      await ensureAlerta(chave, {
        tipo: 'info',
        titulo: `Sequência recomendada · ${atual.titulo}`,
        mensagem: `Você iniciou "${atual.titulo}" sem completar "${anterior.titulo}".`,
      })
    }
  }

  return alertasAtivos
}

function hojeISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function diffDias(a: string, b: string): number {
  return Math.round(
    (new Date(`${b}T12:00:00`).getTime() - new Date(`${a}T12:00:00`).getTime()) / 86_400_000,
  )
}

function fmtData(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

async function checkContratos(): Promise<string[]> {
  const [contratos, parcelas] = await Promise.all([
    db.contratos.where('status').equals('ativo').toArray(),
    getParcelas(),
  ])
  const hoje = hojeISO()
  const alertasAtivos: string[] = []

  for (const c of contratos) {
    const minhas = parcelas.filter((p) => p.contratoId === c.id)

    for (const p of minhas) {
      if (p.status === 'paga' || !p.dataPrevista) continue
      const dias = diffDias(hoje, p.dataPrevista)
      if (dias < 0) {
        const chave = chaveParcelaVencida(c.id!, p.id!)
        alertasAtivos.push(chave)
        await ensureAlerta(chave, {
          tipo: 'danger',
          titulo: `Parcela vencida · ${c.prestador}`,
          mensagem: `"${p.descricao}" (${fmtCur(p.valor)}) estava prevista para ${fmtData(p.dataPrevista)}. ${p.status === 'liberada' ? 'Serviço já medido: registre o pagamento ou renegocie a data.' : 'Confira se o serviço foi executado antes de pagar.'}`,
        })
      } else if (dias <= PARCELA_AVISO_DIAS) {
        const chave = chaveParcelaVencendo(c.id!, p.id!)
        alertasAtivos.push(chave)
        await ensureAlerta(chave, {
          tipo: 'warning',
          titulo: `Pagamento em ${dias === 0 ? 'hoje' : `${dias} dia${dias > 1 ? 's' : ''}`} · ${c.prestador}`,
          mensagem: `"${p.descricao}" (${fmtCur(p.valor)}) vence em ${fmtData(p.dataPrevista)}. Meça o serviço no local antes de liberar.`,
        })
      }
    }

    if (!c.escopo?.trim()) {
      const chave = `contrato:sem-escopo:${c.id!}`
      alertasAtivos.push(chave)
      await ensureAlerta(chave, {
        tipo: 'warning',
        titulo: `Contrato sem escopo · ${c.prestador}`,
        mensagem: `"${c.titulo}" não tem o que foi combinado por escrito. Sem escopo, qualquer extra vira briga.`,
      })
    }

    const resumo = resumirContrato(c, minhas)
    if (resumo.adiantamentoPct > 0 && resumo.pago > 0) {
      const chave = chaveContratoAdiantado(c.id!)
      alertasAtivos.push(chave)
      const grave = resumo.adiantamentoPct > SINAL_MAXIMO_PCT
      await ensureAlerta(chave, {
        tipo: grave ? 'danger' : 'warning',
        titulo: `Pagamento adiantado · ${c.titulo}`,
        mensagem: `${resumo.adiantamentoPct}% do contrato com ${c.prestador} foi pago sem medição prévia. Só libere a próxima parcela depois de conferir o serviço no local.`,
      })
    }
  }

  return alertasAtivos
}

async function checkCronograma(): Promise<string[]> {
  const [fases, config] = await Promise.all([getCronograma(), getObraConfig()])
  if (!config) return []
  const alertasAtivos: string[] = []
  const labels = new Map(FASES_CONSTRUTIVAS.map((f) => [f.id, f.label]))
  const hoje = hojeISO()

  for (const f of fases) {
    if (f.avancoPct >= 100 || !f.fimPrevisto || f.fimPrevisto >= hoje) continue
    const chave = chaveFaseAtrasada(f.faseId)
    alertasAtivos.push(chave)
    const atraso = diffDias(f.fimPrevisto, hoje)
    await ensureAlerta(chave, {
      tipo: atraso > 14 ? 'danger' : 'warning',
      titulo: `Fase atrasada · ${labels.get(f.faseId) ?? f.faseId}`,
      mensagem: `Fim previsto em ${fmtData(f.fimPrevisto)}, avanço em ${f.avancoPct}%. ${atraso} dia${atraso > 1 ? 's' : ''} de atraso. Atualize o avanço ou replaneje as datas seguintes.`,
    })
  }

  const resumo = resumirCronograma(fases, config.dataInicio, config.dataPrevisaoTermino)
  if (resumo.atrasoDias > 0) {
    alertasAtivos.push(CHAVE_OBRA_ATRASADA)
    await ensureAlerta(CHAVE_OBRA_ATRASADA, {
      tipo: 'danger',
      titulo: 'Obra passou do término previsto',
      mensagem: `Término previsto em ${fmtData(resumo.terminoPrevisto!)}, avanço geral em ${resumo.avancoGeral}%. Cada mês extra custa aluguel, juros e mão de obra parada.`,
    })
  }

  return alertasAtivos
}

async function checkDecisoes(): Promise<string[]> {
  const [decisoes, fases, config] = await Promise.all([
    getDecisoes(),
    getCronograma(),
    getObraConfig(),
  ])
  const faseId =
    resumirCronograma(fases, config?.dataInicio, config?.dataPrevisaoTermino).faseAtualId ??
    config?.faseConstrutiva ??
    null
  const alertasAtivos: string[] = []
  for (const c of decisoesQueTravemAgora(decisoes, faseId)) {
    const chave = `decisao:pendente:${c.chave}`
    alertasAtivos.push(chave)
    await ensureAlerta(chave, {
      tipo: 'warning',
      titulo: `Decisão sem registro · ${c.titulo}`,
      mensagem: c.motivo,
    })
  }
  return alertasAtivos
}

async function checkRitmoECaixa(): Promise<string[]> {
  const [ritmo, caixa] = await Promise.all([getRitmoObra(), getCaixaDoMes()])
  const alertasAtivos: string[] = []
  if (ritmo.parado) {
    alertasAtivos.push('diario:parado')
    await ensureAlerta('diario:parado', {
      tipo: 'warning',
      titulo: 'Obra sem registro no diário',
      mensagem:
        ritmo.diasSemDiario == null
          ? 'Nenhuma anotação no diário. Sem foto e texto, não há prova em disputa.'
          : `Última anotação há ${ritmo.diasSemDiario} dias. Registre o que aconteceu no canteiro.`,
    })
  }
  if (caixa.comeReserva && caixa.saidaLiberada > 0) {
    alertasAtivos.push('fin:caixa-mes')
    await ensureAlerta('fin:caixa-mes', {
      tipo: 'warning',
      titulo: 'Pagamentos do mês comem a reserva',
      mensagem: `Há ${fmtCur(caixa.saidaLiberada)} já medido para pagar neste mês. Se pagar tudo, entra na faixa de imprevistos.`,
    })
  }
  return alertasAtivos
}

// ── Public API ────────────────────────────────────────────────

let geracaoEmAndamento: Promise<void> | null = null

/**
 * Gera/resolve alertas automáticos. Single-flight: chamadas concorrentes
 * (boot + página de alertas, por exemplo) compartilham a mesma execução,
 * evitando alertas duplicados para a mesma chave.
 */
export function gerarAlertasAutomaticos(): Promise<void> {
  if (geracaoEmAndamento) return geracaoEmAndamento
  geracaoEmAndamento = gerarAlertas().finally(() => {
    geracaoEmAndamento = null
  })
  return geracaoEmAndamento
}

async function gerarAlertas(): Promise<void> {
  const [
    chavesFinanceiro,
    chavesNf,
    chavesSeq,
    chavesContratos,
    chavesCronograma,
    chavesDecisoes,
    chavesRitmo,
  ] = await Promise.all([
    checkFinanceiro(),
    checkNfFaltando(),
    checkModuloSequencia(),
    checkContratos(),
    checkCronograma(),
    checkDecisoes(),
    checkRitmoECaixa(),
  ])

  const todasChavesAtivas = new Set([
    ...chavesFinanceiro,
    ...chavesNf,
    ...chavesSeq,
    ...chavesContratos,
    ...chavesCronograma,
    ...chavesDecisoes,
    ...chavesRitmo,
  ])

  const todosAtivos = await db.alertas.where('status').equals('ativo').toArray()

  // Saneamento: remove duplicatas da mesma chave (herança de execuções concorrentes antigas)
  const vistas = new Set<string>()
  const duplicados = todosAtivos.filter((a) => {
    if (!a.chave || a.id == null) return false
    if (vistas.has(a.chave)) return true
    vistas.add(a.chave)
    return false
  })
  if (duplicados.length > 0) await db.alertas.bulkDelete(duplicados.map((a) => a.id!))

  // Auto-resolve alerts whose conditions no longer apply
  const obsoletas = todosAtivos
    .filter((a) => a.chave && !todasChavesAtivas.has(a.chave))
    .map((a) => a.chave!)

  await resolverAlertasByChave(obsoletas)
}

// ── Formatting helper (local, not exported) ───────────────────

function fmtCur(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
